import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type UIEvent,
} from 'react';
import { cn } from '@voll-intelligence/ui-kit/utils';
import { Drawer, DrawerContent } from '@voll-intelligence/ui-kit/ui/drawer';
import { MapFab } from './map_fab.js';
import { useStableCallback } from '../use_stable_callback.js';

/**
 * O palco MAP-CENTRIC do resultado no celular (#3106): o mapa toma o palco
 * inteiro e a página vive numa folha de três paradas por cima dele — o modelo
 * do Airbnb.
 *
 * As paradas, de baixo para cima:
 * - `peek` (espiar): só o cabeçalho da folha (título + meta) — o mapa é a tela;
 * - `hybrid` (híbrido, a parada inicial): metade mapa, metade lista;
 * - `full` (cheia): a folha encosta na borda de baixo do cromo do host e a
 *   lista rola por dentro; um botão flutuante `Mapa` devolve ao híbrido.
 *
 * O que é do palco: a folha nunca fecha (um fling para baixo vai ao espiar, não
 * ao chão), o mapa segue tocável nas duas paradas baixas (`modal={false}`), e a
 * folha mora DENTRO do palco — `container` do `Drawer` mais o `transform` da
 * raiz, que faz o viewport `fixed` da folha caber nele. É isso que a segura
 * abaixo da top bar do host em vez de sobre a tela inteira, e o que faz as
 * frações das paradas serem do palco, não da janela.
 *
 * O mapa recebe a altura da folha na parada atual (`sheetHeight`) para
 * enquadrar os pinos ACIMA dela — o "recentralizar" do híbrido — e o palco
 * publica o que sobra de mapa em `--hui-map-visible`, para os controles de
 * vista do mapa se centrarem no vão livre e não sob a folha.
 *
 * Só a parada cheia ROLA (#3274). O cabeçalho mora dentro do scroller, antes
 * da lista, e nas paradas baixas o scroller é `overflow-hidden`: o Base UI só
 * entrega o dedo à lista quando acha um ancestral com `overflow` `auto`/`scroll`
 * (`isScrollable`, `utils/scrollable`), então sem overflow todo gesto vertical
 * é da folha — dedo para cima no híbrido expande, não rola — e o cabeçalho fica
 * onde o espiar precisa dele. Na cheia a lista rola e leva título e meta junto:
 * a folha lê como a própria tela, sem alça, raio ou borda.
 *
 * Rolando na cheia, o palco publica a DIREÇÃO do scroll da lista
 * (`onContentScrollDirection`): é o gatilho de quem esconde cromo fora da folha
 * enquanto o dedo desce e o devolve quando sobe — a fila de chips de filtro na
 * top bar do host (#3274). O palco não esconde nada: só diz para onde a lista
 * vai.
 *
 * Só primitivo do DS + utilitária: nenhuma classe `iuk-*`.
 */
type MapCentricSheetStage = 'peek' | 'hybrid' | 'full';

/** Para onde a lista da folha cheia está rolando: `down` (o dedo desce, o
 *  conteúdo sobe) ou `up` (qualquer deslocamento no sentido do topo, e o
 *  próprio topo). */
export type MapCentricScrollDirection = 'down' | 'up';

export type MapCentricStageFrame = {
  /** A altura da folha em px na parada atual — o que ela cobre do mapa. */
  readonly sheetHeight: number;
  /** Recolhe a folha ao espiar — o mapa pede quando algo dele precisa da tela
   *  (a prévia do hotel ancorada no pé, #3108). */
  readonly collapseSheet: () => void;
  /** Leva a folha ao híbrido — o mapa pede quando um gesto nele muda a lista
   *  (o pino do carro que filtra pela agência, #3995). */
  readonly showList: () => void;
};

export type MapCentricStageProps = {
  /** O mapa, no palco inteiro. */
  map: (frame: MapCentricStageFrame) => ReactNode;
  /** O cabeçalho da folha: o que cabe no espiar (título + linha da meta). A
   *  parada de espiar é uma altura fixa (`PEEK_PX`), então o cabeçalho tem de
   *  caber nela — título numa linha, meta numa linha. Fica preso nas paradas
   *  baixas e rola com a lista na cheia (ver o bloco do componente). */
  header: ReactNode;
  /** O corpo rolável da folha (a lista). */
  content: ReactNode;
  /** O id do h1 do cabeçalho: é o nome acessível da folha. */
  labelledBy: string;
  copy: {
    /** O botão flutuante da parada cheia que devolve ao híbrido ("Mapa"). Sem
     *  ele o botão não é montado — a alça continua devolvendo por arraste. */
    showMapLabel?: string;
  };
  /** A parada inicial; `hybrid` por padrão. */
  defaultStage?: MapCentricSheetStage;
  /** A direção do scroll da lista na cheia, só quando muda; fora da cheia a
   *  lista não rola e a direção volta a `up`. */
  onContentScrollDirection?: (direction: MapCentricScrollDirection) => void;
};

/** O espiar em px: a borda de cima da folha (1) + alça (12) + título (32) +
 *  vão (10) + meta (32) + respiro (4 acima, 12 abaixo). Fixo, e não medido:
 *  uma parada que muda com o conteúdo salta sob o dedo. */
const PEEK_PX = 103;
const PEEK = `${String(PEEK_PX)}px`;
const HYBRID = 0.55;
const FULL = 1;
/** Só `FULL` (`1`) liga o `data-expanded` do Base UI — é dele que a folha cheia
 *  tira a fusão com a tela (sem raio, sem borda). */
const SNAP_POINTS = [PEEK, HYBRID, FULL];
type Snap = (typeof SNAP_POINTS)[number];

const SNAP_OF: Record<MapCentricSheetStage, Snap> = { peek: PEEK, hybrid: HYBRID, full: FULL };

function stageOf(snap: Snap): MapCentricSheetStage {
  if (snap === FULL) return 'full';
  if (snap === HYBRID) return 'hybrid';
  return 'peek';
}

function sheetHeightOf(snap: Snap, stageHeight: number): number {
  return typeof snap === 'string' ? PEEK_PX : Math.round(snap * stageHeight);
}

/** A altura do palco, medida (ResizeObserver): é dela que saem as frações. */
function useStageHeight(stage: HTMLDivElement | null): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    if (!stage) return undefined;
    const measure = () => {
      setHeight(stage.clientHeight);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => {
      observer.disconnect();
    };
  }, [stage]);
  return height;
}

/**
 * A parada da folha, controlada. O Base UI pede FECHAR (`null`) num fling para
 * baixo ou ao soltar mais perto do chão do que da parada mais baixa; a folha
 * não fecha — vai para o espiar, que é o que o dedo pediu. O `onOpenChange`
 * cancela o fechamento (`details.cancel()`), e aí o Base UI tenta devolver a
 * parada de ORIGEM: essa devolução é engolida uma vez, senão um fling do
 * híbrido quicava de volta ao híbrido em vez de descer ao espiar.
 */
function useSheetSnap(defaultStage: MapCentricSheetStage) {
  const [snap, setSnap] = useState<Snap>(SNAP_OF[defaultStage]);
  const restoreToSwallowRef = useRef<Snap | null>(null);
  const onSnapPointChange = (next: Snap | null) => {
    if (next === null) {
      restoreToSwallowRef.current = snap;
      setSnap(PEEK);
      return;
    }
    if (restoreToSwallowRef.current !== null && next === restoreToSwallowRef.current) {
      restoreToSwallowRef.current = null;
      return;
    }
    restoreToSwallowRef.current = null;
    setSnap(next);
  };
  return { snap, setSnap, onSnapPointChange };
}

/** A lista só rola na parada cheia; ao descer dela o scroll volta ao topo. Sem
 *  isto, um scroller `overflow-hidden` guardaria o `scrollTop` da cheia e o
 *  cabeçalho ficaria fora da vista no híbrido e no espiar. */
function useScrollResetBelowFull(stage: MapCentricSheetStage) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (stage !== 'full' && scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [stage]);
  return scrollerRef;
}

/** O limiar de direção: abaixo dele o deslocamento não conta. Um dedo parado
 *  ainda treme alguns px, e sem o limiar a barra oscilava a cada toque. */
const SCROLL_DIRECTION_THRESHOLD_PX = 8;

/**
 * A direção do scroll da lista, publicada só quando muda. Perto do topo a
 * direção é sempre `up`: voltar ao topo tem de devolver o que rolar escondeu.
 * Um `scrollTop` que DIMINUI já encostado no pé não é o dedo: é o scroller que
 * cresceu (o host recolheu a barra e o palco ganhou a altura dela) e clampou o
 * scroll — contado como `up`, devolvia a barra que acabou de sair, e a lista
 * curta oscilava para sempre no fim.
 */
function useContentScrollDirection(
  stage: MapCentricSheetStage,
  onDirection: MapCentricStageProps['onContentScrollDirection'],
) {
  const lastTopRef = useRef(0);
  const directionRef = useRef<MapCentricScrollDirection>('up');
  const notify = useStableCallback((direction: MapCentricScrollDirection) => {
    onDirection?.(direction);
  });
  const publish = useCallback(
    (direction: MapCentricScrollDirection) => {
      if (directionRef.current === direction) return;
      directionRef.current = direction;
      notify(direction);
    },
    [notify],
  );
  useEffect(() => {
    if (stage === 'full') return;
    lastTopRef.current = 0;
    publish('up');
  }, [stage, publish]);
  // Sem palco não há lista rolando: ao desmontar (a tela deixou o palco
  // estreito) a direção volta a `up`, senão quem escondeu cromo por `down`
  // ficaria com ele escondido quando o palco voltasse.
  useEffect(
    () => () => {
      publish('up');
    },
    [publish],
  );
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (stage !== 'full') return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop <= SCROLL_DIRECTION_THRESHOLD_PX) {
      lastTopRef.current = scrollTop;
      publish('up');
      return;
    }
    const delta = scrollTop - lastTopRef.current;
    if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD_PX) return;
    lastTopRef.current = scrollTop;
    const clampedAtBottom = delta < 0 && scrollTop + clientHeight >= scrollHeight - 1;
    if (clampedAtBottom) return;
    publish(delta > 0 ? 'down' : 'up');
  };
  return onScroll;
}

export function MapCentricStage({
  map,
  header,
  content,
  labelledBy,
  copy,
  defaultStage = 'hybrid',
  onContentScrollDirection,
}: Readonly<MapCentricStageProps>): ReactNode {
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const stageHeight = useStageHeight(stageEl);
  const { snap, setSnap, onSnapPointChange } = useSheetSnap(defaultStage);
  const stage = stageOf(snap);
  const sheetHeight = sheetHeightOf(snap, stageHeight);
  const scrollerRef = useScrollResetBelowFull(stage);
  const onContentScroll = useContentScrollDirection(stage, onContentScrollDirection);
  return (
    <div
      ref={setStageEl}
      data-stage={stage}
      className="relative min-h-0 min-w-0 flex-1 overflow-hidden transform-[translateZ(0)]"
      style={
        {
          '--hui-map-visible': `${String(Math.max(0, stageHeight - sheetHeight))}px`,
        } as CSSProperties
      }
    >
      {/* A folha não é modal, então nada tira o foco do que ela cobre: na
          parada cheia o mapa fica inteiro atrás dela e os controles dele
          seguiriam alcançáveis pelo teclado — `inert` os tira do caminho. Nas
          paradas baixas o mapa é a tela e segue vivo. */}
      <div className="absolute inset-0" inert={stage === 'full'}>
        {map({
          sheetHeight,
          collapseSheet: () => {
            setSnap(PEEK);
          },
          showList: () => {
            setSnap(HYBRID);
          },
        })}
      </div>
      {/* A folha só nasce com o palco medido: o `container` é o próprio palco. */}
      {stageEl && (
        <Drawer
          open
          modal={false}
          showSwipeHandle
          snapPoints={SNAP_POINTS}
          snapPoint={snap}
          onSnapPointChange={onSnapPointChange}
          onOpenChange={(open, details) => {
            if (!open) details.cancel();
          }}
          disablePointerDismissal
        >
          <DrawerContent
            container={stageEl}
            aria-labelledby={labelledBy}
            initialFocus={false}
            // Na cheia a alça some (`opacity`, não `display`: a caixa de 12px
            // fica, e com ela o único alvo de arraste do mouse) e a folha perde
            // raio e borda pelo `data-expanded`, passando a ler como tela. O
            // raio precisa de `!`: o Tailwind ordena `data-expanded:` antes do
            // `data-[swipe-direction=down]:rounded-t-xl` do Drawer, com a mesma
            // especificidade, então sem ele o canto do DS vence e o mapa vaza
            // pelos cantos da folha cheia.
            className="bg-background data-[swipe-axis=y]:[--drawer-content-max-height:100%] data-[swipe-axis=y]:data-snap-points:[--drawer-content-height:100%] data-expanded:rounded-t-none! data-expanded:border-t-transparent data-expanded:[&_[data-slot=drawer-swipe-handle]]:opacity-0"
          >
            {/* O contêiner `results-content` é a régua de COLUNA (não de janela) que
                um card pode consultar — a mesma do layout largo; hoje nenhum card a
                usa (carro e ônibus decidem pelo palco).
                `isolate`: as camadas dos cards ficam AQUI dentro (#6185). Sem ele
                o `Selecionar` esticado do card de carro (`z-index: 1`) subia ao
                contexto do popup e pintava por cima do botão `Mapa` (`z-index:
                auto`): o botão seguia visível e o toque nele selecionava o carro de
                baixo. Nem `overflow` nem o `@container` isolam. */}
            <div
              ref={scrollerRef}
              onScroll={onContentScroll}
              className={cn(
                '@container/results-content scrollbar-rail pr-[calc(1rem-var(--scrollbar-rail))] pl-4 isolate flex min-h-0 flex-1 flex-col pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))]',
                stage === 'full' ? 'overflow-y-auto' : 'overflow-hidden',
              )}
            >
              <div className="flex shrink-0 flex-col pt-1 pb-3">{header}</div>
              {content}
            </div>
            {stage === 'full' && copy.showMapLabel && (
              <MapFab
                label={copy.showMapLabel}
                placement="overlay"
                onOpen={() => {
                  setSnap(HYBRID);
                }}
              />
            )}
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
